import { useState } from 'react';
import { useGetPlanetStrengthsQuery } from '@/store/api/planetStrengthsApi';
import Loader from '@/components/Loader';
import {
  Info,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Zap,
  TrendingUp,
  Skull,
  Compass,
  Award,
} from 'lucide-react';

interface StrengthsProps {
  userId: string;
}

export default function Strengths({ userId }: StrengthsProps) {
  const { data: strengthsRaw, isLoading, isError } = useGetPlanetStrengthsQuery(userId, {
    skip: !userId,
  });

  const [activeSection, setActiveSection] = useState<'shadbala' | 'avastha' | 'ishtakashta'>('shadbala');
  const [expandedPlanets, setExpandedPlanets] = useState<Record<string, boolean>>({});

  const strengthsData = (strengthsRaw as any)?.data || strengthsRaw;

  const togglePlanetDetails = (planet: string) => {
    setExpandedPlanets((prev) => ({
      ...prev,
      [planet]: !prev[planet],
    }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    );
  }

  if (isError || !strengthsData) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
        <div className="bg-red-50 text-red-500 px-8 py-4 rounded-xl border border-red-100 font-spectral font-semibold shadow-sm">
          Planetary strength data is currently unavailable. Please try again later.
        </div>
      </div>
    );
  }

  const {
    avastha = [],
    shadbala = [],
    ishtaphala = [],
    kashtaphala = [],
  } = strengthsData;

  // Helper to safely get nested Shadbala component values
  const getBalaValue = (val: any): number => {
    if (typeof val === 'number') return val;
    if (val && typeof val === 'object') {
      return val.virupas ?? val.total_virupas ?? val.partial_total_virupas ?? 0;
    }
    return 0;
  };

  // Helper for Avastha details and badge styling
  const getAvasthaConfig = (name: string) => {
    const n = name.toLowerCase();
    if (n.includes('yuva')) {
      return {
        label: 'Yuva (Adult/Mature)',
        desc: '100% Strength. Maximum potential, actions bear full results.',
        color: 'from-emerald-500 to-green-600 border-emerald-400',
        badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-300',
        bg: 'bg-emerald-50/60',
        icon: Sparkles,
      };
    }
    if (n.includes('kumara')) {
      return {
        label: 'Kumara (Youthful/Teenager)',
        desc: '50% Strength. Highly active, growing power and high ambition.',
        color: 'from-cyan-500 to-blue-600 border-cyan-400',
        badgeClass: 'bg-cyan-100 text-cyan-800 border-cyan-300',
        bg: 'bg-cyan-50/60',
        icon: TrendingUp,
      };
    }
    if (n.includes('bala') || n.includes('sishu')) {
      return {
        label: 'Bala (Infant/Child)',
        desc: '25% Strength. Growing/fresh energy, active but needs support.',
        color: 'from-amber-400 to-orange-500 border-amber-300',
        badgeClass: 'bg-amber-100 text-amber-800 border-amber-300',
        bg: 'bg-amber-50/60',
        icon: Zap,
      };
    }
    if (n.includes('vriddha')) {
      return {
        label: 'Vriddha (Old/Elderly)',
        desc: '40-50% Strength. Wisdom is high, physical power is reduced.',
        color: 'from-orange-500 to-red-600 border-orange-400',
        badgeClass: 'bg-orange-100 text-orange-800 border-orange-300',
        bg: 'bg-orange-50/60',
        icon: Compass,
      };
    }
    // Mrit or other
    return {
      label: 'Mrit (Dead/Inactive)',
      desc: '0-5% Strength. Negligible physical strength, but carries high spiritual lesson.',
      color: 'from-purple-500 to-slate-600 border-purple-400',
      badgeClass: 'bg-purple-100 text-purple-800 border-purple-300',
      bg: 'bg-purple-50/60',
      icon: Skull,
    };
  };

  const getStrengthProfileLabel = (profile: string) => {
    return profile
      ? profile
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ')
      : '';
  };

  const getStrengthProfileBadgeColor = (profile: string) => {
    const p = (profile || '').toLowerCase();
    if (p.includes('maximum'))
      return 'bg-emerald-100 text-emerald-800 border-emerald-300';
    if (p.includes('growing'))
      return 'bg-cyan-100 text-cyan-800 border-cyan-300';
    if (p.includes('reduced'))
      return 'bg-amber-100 text-amber-800 border-amber-300';
    return 'bg-purple-100 text-purple-800 border-purple-300';
  };

  // Build lookup dictionary for Ishta and Kashta to render combined view
  const ishtaMap = ishtaphala.reduce((acc: any, curr: any) => {
    acc[curr.planet] = curr;
    return acc;
  }, {});

  const kashtaMap = kashtaphala.reduce((acc: any, curr: any) => {
    acc[curr.planet] = curr;
    return acc;
  }, {});

  // List of unique planets in ishta/kashta
  const combinedPlanetList = Array.from(
    new Set([...Object.keys(ishtaMap), ...Object.keys(kashtaMap)])
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="border border-[#2DAAFD] rounded-xl p-4 sm:p-6 lg:p-10 bg-white shadow-[0_4px_20px_rgba(45,170,253,0.08)] font-lato">
        {/* Header Section */}
        <div className="flex flex-col items-center mb-6 sm:mb-8">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className="bg-[#082261] p-1.5 sm:p-2 rounded-lg text-white">
              <Award className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <h2 className="text-xl sm:text-2xl lg:text-[28px] font-semibold font-spectral bg-clip-text text-transparent bg-gradient-to-r from-[#082261] to-[#34065c] text-center uppercase tracking-widest">
              PLANETARY STRENGTHS
            </h2>
          </div>
          <p className="font-lato text-xs sm:text-sm text-gray-500 text-center max-w-xl">
            Explore cosmic power (Shadbala), developmental states (Avasthas), and functional fruits (Ishta / Kashta) of your planets.
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-[#082261] to-[#34065c] mt-3 rounded-full opacity-30"></div>
        </div>

        {/* Navigation Sub-Tabs */}
        <div className="flex justify-center gap-1.5 sm:gap-2 bg-gray-100 p-1 sm:p-1.5 rounded-xl border border-gray-200 w-full sm:w-auto mx-auto mb-6 sm:mb-8 max-w-md">
          <button
            onClick={() => setActiveSection('shadbala')}
            className={`flex-1 px-2.5 sm:px-4 py-2 rounded-lg font-spectral font-semibold text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
              activeSection === 'shadbala'
                ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white shadow-md'
                : 'text-[#082261] hover:bg-white/80'
            }`}
          >
            Shadbala
          </button>
          <button
            onClick={() => setActiveSection('avastha')}
            className={`flex-1 px-2.5 sm:px-4 py-2 rounded-lg font-spectral font-semibold text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
              activeSection === 'avastha'
                ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white shadow-md'
                : 'text-[#082261] hover:bg-white/80'
            }`}
          >
            Avasthas
          </button>
          <button
            onClick={() => setActiveSection('ishtakashta')}
            className={`flex-1 px-2.5 sm:px-4 py-2 rounded-lg font-spectral font-semibold text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
              activeSection === 'ishtakashta'
                ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white shadow-md'
                : 'text-[#082261] hover:bg-white/80'
            }`}
          >
            Ishta / Kashta
          </button>
        </div>

        {/* 1. SHADBALA SECTION */}
        {activeSection === 'shadbala' && (
          <div className="space-y-6">
            <div className="bg-[#F8FBFF] p-4 rounded-xl border border-[#2DAAFD]/30 flex gap-3 items-start">
              <Info className="w-5 h-5 text-[#082261] shrink-0 mt-0.5" />
              <p className="font-lato text-sm text-[#082261] leading-relaxed">
                <strong>Shadbala</strong> is a comprehensive system in Vedic Astrology that measures a planet's strength based on six distinct factors: positional, directional, temporal, motional, natural, and aspectual. Values are in <strong>Virupas</strong> (60 Virupas = 1 Rupa). If a planet exceeds 100% of its required strength, it behaves with high efficacy.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {shadbala.map((item: any, index: number) => {
                const isExpanded = !!expandedPlanets[item.planet];
                const pct = item.percentStrength ?? (item.strengthRatio !== undefined ? item.strengthRatio * 100 : 0);
                const isStrong = pct >= 100;

                // Detailed sub-bala values
                const sthana = getBalaValue(item.sthanaBala);
                const dig = getBalaValue(item.digBala);
                const kaala = getBalaValue(item.kaalaBala);
                const cheshta = getBalaValue(item.cheshtaBala);
                const naisargika = getBalaValue(item.naisargikaBala);
                const drik = getBalaValue(item.drikBala);
                const total = item.totalVirupas ?? 0;

                return (
                  <div
                    key={index}
                    className="border border-[#2DAAFD]/30 rounded-xl bg-white hover:border-[#2DAAFD] transition-all duration-300 overflow-hidden shadow-sm"
                  >
                    {/* Planet Summary Row */}
                    <div
                      onClick={() => togglePlanetDetails(item.planet)}
                      className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 cursor-pointer select-none bg-gradient-to-r from-[#F8FBFF] to-[#E6F3FF]"
                    >
                      <div className="flex items-center gap-4 w-full md:w-1/4">
                        <div className="w-10 h-10 rounded-lg bg-[#082261] text-white flex items-center justify-center font-spectral font-bold text-lg shadow-sm">
                          {item.planet.slice(0, 2)}
                        </div>
                        <div>
                          <h4 className="font-spectral font-bold text-lg text-[#082261] tracking-wide">
                            {item.planet}
                          </h4>
                          <span className="font-lato text-xs text-gray-500">
                            Required: {item.requiredRupas} Rupas
                          </span>
                        </div>
                      </div>

                      {/* Bar indicator */}
                      <div className="flex-1 w-full flex flex-col gap-1">
                        <div className="flex justify-between text-xs font-lato text-gray-600">
                          <span>
                            Actual: {item.rupas.toFixed(2)} Rupas ({total.toFixed(0)} Virupas)
                          </span>
                          <span
                            className={`${isStrong ? 'text-emerald-600' : 'text-amber-600'} font-semibold`}
                          >
                            {pct.toFixed(1)}% Strength
                          </span>
                        </div>
                        <div className="relative h-3 w-full bg-gray-200 border border-gray-300 rounded-full overflow-hidden">
                          {/* Required threshold vertical indicator */}
                          <div
                            className="absolute top-0 bottom-0 w-0.5 bg-rose-500 z-10 shadow-[0_0_8px_#f43f5e]"
                            style={{
                              left: `${Math.min(100, 100 / (pct / 100))}%`,
                            }}
                          />
                          {/* Actual strength filled bar */}
                          <div
                            className={`h-full rounded-full transition-all duration-500 bg-gradient-to-r ${
                              isStrong
                                ? 'from-[#082261] to-[#2DAAFD]'
                                : 'from-amber-600 to-amber-400'
                            }`}
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-3 self-end md:self-auto shrink-0">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                            isStrong
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                              : 'bg-amber-100 text-amber-800 border-amber-300'
                          }`}
                        >
                          {isStrong ? 'Sufficient' : 'Weak'}
                        </span>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-[#082261]" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-[#082261]" />
                        )}
                      </div>
                    </div>

                    {/* Detailed sub-bala expansion */}
                    {isExpanded && (
                      <div className="border-t border-[#2DAAFD]/20 bg-[#F8FBFF] p-4 md:p-6 space-y-4">
                        <h5 className="font-spectral font-bold text-[#082261] text-sm uppercase tracking-wider mb-2">
                          Breakdown of Shadbala Components (Virupas)
                        </h5>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {/* Sthana Bala */}
                          <div className="bg-white p-3 rounded-lg border border-[#2DAAFD]/20 shadow-xs">
                            <div className="flex justify-between text-xs font-lato mb-1">
                              <span className="text-[#082261] font-semibold">
                                Sthana Bala (Positional)
                              </span>
                              <span className="text-[#34065c] font-medium">
                                {sthana.toFixed(1)}
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-cyan-500 rounded-full"
                                style={{
                                  width: `${Math.min(100, (sthana / 200) * 100)}%`,
                                }}
                              />
                            </div>
                          </div>

                          {/* Dig Bala */}
                          <div className="bg-white p-3 rounded-lg border border-[#2DAAFD]/20 shadow-xs">
                            <div className="flex justify-between text-xs font-lato mb-1">
                              <span className="text-[#082261] font-semibold">
                                Dig Bala (Directional)
                              </span>
                              <span className="text-[#34065c] font-medium">
                                {dig.toFixed(1)}
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-purple-500 rounded-full"
                                style={{
                                  width: `${Math.min(100, (dig / 60) * 100)}%`,
                                }}
                              />
                            </div>
                          </div>

                          {/* Kaala Bala */}
                          <div className="bg-white p-3 rounded-lg border border-[#2DAAFD]/20 shadow-xs">
                            <div className="flex justify-between text-xs font-lato mb-1">
                              <span className="text-[#082261] font-semibold">
                                Kaala Bala (Temporal)
                              </span>
                              <span className="text-[#34065c] font-medium">
                                {kaala.toFixed(1)}
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-emerald-500 rounded-full"
                                style={{
                                  width: `${Math.min(100, (kaala / 250) * 100)}%`,
                                }}
                              />
                            </div>
                          </div>

                          {/* Cheshta Bala */}
                          <div className="bg-white p-3 rounded-lg border border-[#2DAAFD]/20 shadow-xs">
                            <div className="flex justify-between text-xs font-lato mb-1">
                              <span className="text-[#082261] font-semibold">
                                Cheshta Bala (Motional)
                              </span>
                              <span className="text-[#34065c] font-medium">
                                {cheshta.toFixed(1)}
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-amber-500 rounded-full"
                                style={{
                                  width: `${Math.min(100, (cheshta / 60) * 100)}%`,
                                }}
                              />
                            </div>
                          </div>

                          {/* Naisargika Bala */}
                          <div className="bg-white p-3 rounded-lg border border-[#2DAAFD]/20 shadow-xs">
                            <div className="flex justify-between text-xs font-lato mb-1">
                              <span className="text-[#082261] font-semibold">
                                Naisargika Bala (Natural)
                              </span>
                              <span className="text-[#34065c] font-medium">
                                {naisargika.toFixed(1)}
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-indigo-500 rounded-full"
                                style={{
                                  width: `${Math.min(100, (naisargika / 60) * 100)}%`,
                                }}
                              />
                            </div>
                          </div>

                          {/* Drik Bala */}
                          <div className="bg-white p-3 rounded-lg border border-[#2DAAFD]/20 shadow-xs">
                            <div className="flex justify-between text-xs font-lato mb-1">
                              <span className="text-[#082261] font-semibold">
                                Drik Bala (Aspectual)
                              </span>
                              <span
                                className={`font-medium ${drik < 0 ? 'text-rose-600' : 'text-emerald-600'}`}
                              >
                                {drik.toFixed(1)}
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${drik < 0 ? 'bg-rose-500' : 'bg-green-500'}`}
                                style={{
                                  width: `${Math.min(100, Math.abs(drik / 60) * 100)}%`,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 2. AVASTHAS SECTION */}
        {activeSection === 'avastha' && (
          <div className="space-y-6">
            <div className="bg-[#F8FBFF] p-4 rounded-xl border border-[#2DAAFD]/30 flex gap-3 items-start">
              <Info className="w-5 h-5 text-[#082261] shrink-0 mt-0.5" />
              <p className="font-lato text-sm text-[#082261] leading-relaxed">
                <strong>Baladi Avastha</strong> corresponds to the physical/developmental age state of the planet based on its degree positioning inside odd or even zodiac signs. It defines how easily a planet's results manifest in reality. A planet in <strong>Yuva</strong> avastha is in its prime and acts with maximum strength.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {avastha.map((item: any, idx: number) => {
                const config = getAvasthaConfig(item.name);
                const AvasthaIcon = config.icon;
                const progressPct = (item.progressWithinState ?? 0) * 100;

                return (
                  <div
                    key={idx}
                    className="border border-[#2DAAFD]/30 rounded-xl bg-white hover:border-[#2DAAFD] transition-all duration-300 p-5 flex flex-col justify-between shadow-sm"
                  >
                    <div>
                      {/* Top header row */}
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="font-spectral font-bold text-xl text-[#082261]">
                            {item.planet}
                          </h4>
                          <span className="font-lato text-xs text-gray-500">
                            {item.sign} ({item.signDegree.toFixed(2)}°)
                          </span>
                        </div>

                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getStrengthProfileBadgeColor(item.strengthProfile)}`}
                        >
                          {getStrengthProfileLabel(item.strengthProfile)}
                        </span>
                      </div>

                      {/* State Description */}
                      <div className={`p-3 rounded-lg border border-[#2DAAFD]/20 ${config.bg} mb-4`}>
                        <div className="flex items-center gap-2 mb-1">
                          <AvasthaIcon className="w-4 h-4 text-[#082261]" />
                          <span className="font-spectral font-bold text-[#082261] text-sm">
                            {config.label}
                          </span>
                        </div>
                        <p className="font-lato text-xs text-gray-600 leading-relaxed">
                          {config.desc}
                        </p>
                      </div>
                    </div>

                    {/* Progress details */}
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-xs font-lato text-gray-500 mb-1">
                          <span>Progress within State</span>
                          <span className="font-semibold text-[#082261]">{progressPct.toFixed(0)}%</span>
                        </div>
                        <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden border border-gray-300">
                          <div
                            className={`h-full rounded-full bg-gradient-to-r ${config.color}`}
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex justify-between text-xs font-lato border-t border-gray-100 pt-2 text-gray-500">
                        <span>
                          Sign parity:{' '}
                          <strong className="text-[#082261] capitalize">
                            {item.signParity}
                          </strong>
                        </span>
                        <span>
                          Rule direction:{' '}
                          <strong className="text-[#082261] capitalize">
                            {item.ruleDirection}
                          </strong>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 3. ISHTA & KASHTA SECTION */}
        {activeSection === 'ishtakashta' && (
          <div className="space-y-6">
            <div className="bg-[#F8FBFF] p-4 rounded-xl border border-[#2DAAFD]/30 flex gap-3 items-start">
              <Info className="w-5 h-5 text-[#082261] shrink-0 mt-0.5" />
              <p className="font-lato text-sm text-[#082261] leading-relaxed">
                <strong>Ishta Phala</strong> represents the auspicious, beneficial, and positive karmic outputs that a planet brings to your life. <strong>Kashta Phala</strong> represents the challenging, difficult, and transformative struggles. A higher Ishta ratio makes the planet highly beneficial in its periods (Dasha).
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {combinedPlanetList.map((planet: any, index: number) => {
                const ishtaItem = ishtaMap[planet];
                const kashtaItem = kashtaMap[planet];

                const ishtaPct = ishtaItem?.percent ?? 0;
                const kashtaPct = kashtaItem?.percent ?? 0;

                return (
                  <div
                    key={index}
                    className="border border-[#2DAAFD]/30 rounded-xl bg-white hover:border-[#2DAAFD] p-5 flex flex-col justify-between gap-4 transition-all duration-300 shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#082261] text-white flex items-center justify-center font-spectral font-bold shadow-sm">
                        {planet.slice(0, 2)}
                      </div>
                      <h4 className="font-spectral font-bold text-xl text-[#082261]">
                        {planet}
                      </h4>
                    </div>

                    <div className="space-y-3">
                      {/* Double bar indicator */}
                      <div>
                        {/* Ishta bar */}
                        <div className="flex justify-between text-xs font-lato text-gray-600 mb-1">
                          <span className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 block" />
                            Ishta Phala (Auspicious Energy)
                          </span>
                          <span className="text-emerald-600 font-semibold">
                            {ishtaPct.toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-2.5 w-full bg-gray-200 border border-gray-300 rounded-full overflow-hidden mb-3">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-emerald-400"
                            style={{ width: `${ishtaPct}%` }}
                          />
                        </div>

                        {/* Kashta bar */}
                        <div className="flex justify-between text-xs font-lato text-gray-600 mb-1">
                          <span className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 block" />
                            Kashta Phala (Challenging Energy)
                          </span>
                          <span className="text-rose-600 font-semibold">
                            {kashtaPct.toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-2.5 w-full bg-gray-200 border border-gray-300 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-rose-600 to-rose-400"
                            style={{ width: `${kashtaPct}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex justify-between text-xs font-lato border-t border-gray-100 pt-3 mt-1 text-gray-500">
                        <span>
                          Ishta Rupas:{' '}
                          <strong className="text-[#082261] font-medium">
                            {(ishtaItem?.rupas ?? 0).toFixed(2)}
                          </strong>
                        </span>
                        <span>
                          Kashta Rupas:{' '}
                          <strong className="text-[#082261] font-medium">
                            {(kashtaItem?.rupas ?? 0).toFixed(2)}
                          </strong>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
